/**
 * AppearanceSelector — elección de aspecto del CMDB.
 *
 * Dos ejes independientes que se combinan libremente:
 *
 *   · Estilo (skin)  → forma: radios, sombras, superficies, densidad.
 *   · Color (paleta) → fondo, superficies y acento.
 *
 * Al elegir se aplica al instante (no hay botón de "guardar"): el usuario ve el
 * cambio mientras lo prueba y puede volver atrás. La elección se guarda en el
 * navegador; si no ha elegido nunca, se sigue el estilo por defecto de su
 * empresa (`companyDefault`, lo resuelve `App.jsx`).
 */

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PALETTES, DEFAULT_PALETTE } from '../theme/palettes';
import { SKINS, DEFAULT_SKIN } from '../theme/skins';
import { applyAppearance, readStoredAppearance, saveAppearance } from '../theme/appearance';
import {
  BG_MOTIONS,
  BG_STYLES,
  DEFAULT_BG_MOTION,
  DEFAULT_BG_STYLE,
  readStoredBackground,
  saveBackground,
} from '../theme/background';
import api from '../services/api';
import {
  DENSITIES,
  DEFAULT_DENSITY,
  readStoredDensity,
  saveDensity,
} from '../theme/density';
import './AppearanceSelector.css';

/** Miniatura de una ventana con la forma del skin y el acento de la paleta. */
function SkinThumb({ skin }) {
  const p = skin.preview;
  const innerRadius = Math.max(4, Math.round(p.radius * 0.55));
  return (
    <div
      className="ap-thumb"
      style={{
        background: p.page,
        borderRadius: `${p.radius}px`,
        backdropFilter: p.blur ? 'blur(6px)' : undefined,
      }}
    >
      <div
        className="ap-thumb__side"
        style={{ background: p.card, borderColor: p.border }}
      >
        <span className="ap-thumb__dot" style={{ background: 'var(--accent)' }} />
        <span className="ap-thumb__line" style={{ background: p.muted }} />
        <span className="ap-thumb__line" style={{ background: p.muted }} />
        <span className="ap-thumb__line" style={{ background: p.muted }} />
      </div>
      <div className="ap-thumb__main">
        <span
          className="ap-thumb__card"
          style={{
            background: p.card,
            borderColor: p.border,
            borderRadius: `${innerRadius}px`,
          }}
        >
          <span className="ap-thumb__title" style={{ background: p.text }} />
          <span className="ap-thumb__sub" style={{ background: p.muted }} />
        </span>
        <span
          className="ap-thumb__card"
          style={{
            background: p.card,
            borderColor: p.border,
            borderRadius: `${innerRadius}px`,
          }}
        >
          <span className="ap-thumb__title ap-thumb__title--short" style={{ background: p.text }} />
          <span className="ap-thumb__sub" style={{ background: p.muted }} />
        </span>
        <span
          className="ap-thumb__chip"
          style={{
            background: p.glow
              ? 'linear-gradient(135deg, var(--accent), var(--accent-2))'
              : 'var(--accent)',
            borderRadius: `${Math.max(4, Math.round(p.radius * 0.5))}px`,
          }}
        />
      </div>
    </div>
  );
}

/** Miniatura de paleta: solo color, con la misma forma de ventana. */
function PaletteThumb({ palette, active }) {
  const isLight = palette.base === 'light';
  const hairline = isLight ? 'rgba(0,0,0,.12)' : 'rgba(255,255,255,.09)';
  const softText = isLight ? 'rgba(0,0,0,.16)' : 'rgba(255,255,255,.22)';
  return (
    <div
      className="ap-palette-thumb"
      style={{ background: palette.bg, borderRadius: '8px' }}
    >
      <div className="ap-palette-thumb__side" style={{ background: palette.surface, borderColor: hairline }}>
        <span className="ap-palette-thumb__dot" style={{ background: palette.accent }} />
        <span className="ap-palette-thumb__line" style={{ background: softText }} />
        <span className="ap-palette-thumb__line" style={{ background: softText }} />
      </div>
      <div className="ap-palette-thumb__main">
        <span className="ap-palette-thumb__bar" style={{ background: palette.surface }} />
        <span className="ap-palette-thumb__bar ap-palette-thumb__bar--short" style={{ background: softText }} />
        <span className="ap-palette-thumb__btn" style={{ background: palette.accent }} />
      </div>
    </div>
  );
}

function AppearanceSelector({ companyId = null, companyDefault = null, serverTheme = null }) {
  const { t } = useTranslation();

  const stored = readStoredAppearance();
  const serverUser = serverTheme?.user || null;

  const [skin, setSkin] = useState(stored.skin || serverUser?.skin || null);
  const [palette, setPalette] = useState(
    stored.palette || serverUser?.palette || companyDefault?.palette || DEFAULT_PALETTE
  );
  // El fondo es preferencia del navegador: no viaja al servidor.
  const [bg, setBg] = useState(() => readStoredBackground());
  // La densidad de las tablas, igual: del navegador (auto por defecto).
  const [density, setDensity] = useState(() => readStoredDensity() || DEFAULT_DENSITY);
  // Sin estilo propio (ni en este navegador ni guardado en el servidor) se sigue
  // el que la empresa tenga por defecto.
  const [followingCompany, setFollowingCompany] = useState(!(stored.skin || serverUser?.skin));

  const companySkin = companyDefault?.skin || DEFAULT_SKIN;
  const companyPalette = companyDefault?.palette || null;
  const effectiveSkin = followingCompany ? companySkin : (skin || DEFAULT_SKIN);
  const canFollowCompany = Boolean(companyDefault?.skin || companyDefault?.palette || serverUser);

  /**
   * Guarda la elección en el navegador (efecto inmediato) y en el servidor
   * (para el resto de equipos del usuario). Si no hay estilo propio se envía
   * null, que en el backend significa "volver al de mi empresa".
   */
  const persist = useCallback(
    (nextSkin, nextPalette) => {
      saveAppearance({ skin: nextSkin, palette: nextPalette });
      // `saveAppearance` aplica lo que se guarda; si no hay estilo propio, lo
      // que toca ver es el de la empresa, no Classic.
      if (!nextSkin) {
        applyAppearance({ skin: companySkin, palette: nextPalette || companyPalette });
      }
      if (!companyId) return;
      api
        .saveThemePreferences(companyId, {
          skin: nextSkin,
          palette: nextPalette,
        })
        .catch(() => {
          // La elección ya está aplicada y guardada en este navegador: si falla
          // la sincronización, no tiene sentido romper la sesión de Ajustes.
        });
    },
    [companyId, companyPalette, companySkin]
  );

  const selectSkin = useCallback(
    (id) => {
      setFollowingCompany(false);
      setSkin(id);
      persist(id, palette);
    },
    [persist, palette]
  );

  const selectPalette = useCallback(
    (id) => {
      setPalette(id);
      persist(followingCompany ? null : skin, id);
    },
    [followingCompany, persist, skin]
  );

  const selectBgStyle = useCallback((id) => {
    setBg(saveBackground({ style: id, motion: bg.motion }));
  }, [bg.motion]);

  const selectBgMotion = useCallback((id) => {
    setBg(saveBackground({ style: bg.style, motion: id }));
  }, [bg.style]);

  /** Densidad de las tablas: se guarda en el navegador y se aplica al instante. */
  const selectDensity = useCallback((id) => {
    setDensity(saveDensity(id));
  }, []);

  const followCompany = useCallback(() => {
    setFollowingCompany(true);
    setSkin(null);
    setPalette(companyPalette || DEFAULT_PALETTE);
    // "Usar el de mi empresa" es borrar la preferencia propia, no guardar la de
    // la empresa como si fuera nuestra: se envian ambos a null.
    persist(null, null);
  }, [companyPalette, palette, persist]);

  const reset = useCallback(() => {
    setFollowingCompany(false);
    setSkin(DEFAULT_SKIN);
    setPalette(DEFAULT_PALETTE);
    setBg(saveBackground({ style: DEFAULT_BG_STYLE, motion: DEFAULT_BG_MOTION }));
    persist(DEFAULT_SKIN, DEFAULT_PALETTE);
  }, [persist]);

  return (
    <div className="appearance-selector">
      <p className="ap-intro">
        {t(
          'appearance.intro',
          'El estilo cambia la forma (radios, sombras, densidad) y el color cambia la paleta. Se combinan libremente: puedes usar cualquier estilo con cualquier color.'
        )}
      </p>

      {/* ── Estilo (forma) ──────────────────────────────────────────── */}
      <div className="ap-group">
        <div className="ap-group__head">
          <span className="ap-group__label">{t('appearance.style', 'Estilo')}</span>
          <div className="ap-group__actions">
            {canFollowCompany && (
              <button
                type="button"
                className={`ap-link ${followingCompany ? 'is-on' : ''}`}
                onClick={followCompany}
              >
                {followingCompany ? '✓ ' : ''}
                {t('appearance.followCompany', 'Usar el de mi empresa')}
              </button>
            )}
            <button type="button" className="ap-link" onClick={reset}>
              {t('appearance.reset', 'Restablecer')}
            </button>
          </div>
        </div>

        <div className="ap-grid ap-grid--skins">
          {SKINS.map((s) => {
            const active = effectiveSkin === s.id;
            return (
              <button
                key={s.id}
                type="button"
                className={`ap-card ${active ? 'is-active' : ''}`}
                onClick={() => selectSkin(s.id)}
                title={t(`skins.${s.id}.description`, s.description)}
                aria-pressed={active}
              >
                <SkinThumb skin={s} />
                <span className="ap-card__name">
                  {s.name}
                  {active && followingCompany && companySkin === s.id && (
                    <em className="ap-card__tag">{t('appearance.companyTag', 'empresa')}</em>
                  )}
                  {active && !followingCompany && (
                    <em className="ap-card__tag">{t('appearance.mineTag', 'mío')}</em>
                  )}
                </span>
                <span className="ap-card__tagline">
                  {t(`skins.${s.id}.tagline`, s.tagline)}
                </span>
              </button>
            );
          })}
        </div>
        <p className="ap-hint">
          {t(
            `skins.${effectiveSkin}.description`,
            SKINS.find((s) => s.id === effectiveSkin)?.description
          )}
        </p>
      </div>

      {/* ── Color (paleta) ──────────────────────────────────────────── */}
      <div className="ap-group">
        <span className="ap-group__label">{t('appearance.color', 'Color')}</span>
        <div className="ap-grid ap-grid--palettes">
          {PALETTES.map((p) => {
            const active = palette === p.id;
            return (
              <button
                key={p.id}
                type="button"
                className={`ap-card ap-card--palette ${active ? 'is-active' : ''}`}
                onClick={() => selectPalette(p.id)}
                title={p.name}
                aria-pressed={active}
              >
                <PaletteThumb palette={p} active={active} />
                <span className="ap-card__name">{p.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Fondo ───────────────────────────────────────────────────── */}
      <div className="ap-group">
        <span className="ap-group__label">{t('appearance.background', 'Fondo')}</span>
        <div className="ap-grid ap-grid--backgrounds">
          {BG_STYLES.map((b) => {
            const active = bg.style === b.id;
            return (
              <button
                key={b.id}
                type="button"
                className={`ap-card ap-card--bg ${active ? 'is-active' : ''}`}
                onClick={() => selectBgStyle(b.id)}
                title={b.tagline}
                aria-pressed={active}
              >
                <span className={`ap-bg-thumb ap-bg-thumb--${b.id}`} aria-hidden="true" />
                <span className="ap-card__name">{t(`appearance.bg.${b.id}`, b.name)}</span>
                <span className="ap-card__tagline">
                  {t(`appearance.bg.${b.id}Tagline`, b.tagline)}
                </span>
              </button>
            );
          })}
        </div>

        <div className="ap-inline">
          <span className="ap-inline__label">{t('appearance.motion', 'Movimiento')}</span>
          <div className="ap-seg" role="group" aria-label={t('appearance.motion', 'Movimiento')}>
            {BG_MOTIONS.map((m) => {
              const active = bg.motion === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  className={`ap-seg__btn ${active ? 'is-on' : ''}`}
                  onClick={() => selectBgMotion(m.id)}
                  aria-pressed={active}
                  disabled={bg.style === 'plain'}
                  title={
                    bg.style === 'plain'
                      ? t('appearance.motionNeedsTexture', 'Con el fondo liso no hay nada que mover')
                      : undefined
                  }
                >
                  {t(`appearance.motionOpt.${m.id}`, m.name)}
                </button>
              );
            })}
          </div>
        </div>
        <p className="ap-hint">
          {t(
            'appearance.backgroundHint',
            'El fondo toma los colores de la paleta elegida. Si tu sistema pide menos movimiento, se respeta aunque elijas «Vivo».'
          )}
        </p>
      </div>

      {/* ── Densidad de las tablas ───────────────────────────────────── */}
      <div className="ap-group">
        <span className="ap-group__label">{t('appearance.density', 'Densidad de las tablas')}</span>
        <div className="ap-inline">
          <div
            className="ap-seg"
            role="group"
            aria-label={t('appearance.density', 'Densidad de las tablas')}
          >
            {DENSITIES.map((d) => {
              const active = density === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  className={`ap-seg__btn ${active ? 'is-on' : ''}`}
                  onClick={() => selectDensity(d.id)}
                  aria-pressed={active}
                  title={d.tagline}
                >
                  {t(`appearance.densityOpt.${d.id}`, d.name)}
                </button>
              );
            })}
          </div>
        </div>
        <p className="ap-hint">
          {t(
            'appearance.densityHint',
            '«Automática» aprieta las filas solo en portátiles pequeños o ventanas estrechas. En un monitor grande las deja cómodas.'
          )}
        </p>
      </div>
    </div>
  );
}

export default AppearanceSelector;
